/**
 * Frontend data models for All Tests Dashboard
 * Matches backend DTOs from pdq-core
 */

import type { MaxxAnalysisRowState } from './services/maxx-test-analysis.model';

// ============================================================================
// API Response Interfaces
// ============================================================================

export interface AllTestsOverviewResponse {
  tests: TestListItem[];
  pagination: PaginationInfo;
  runningTestsCount?: number;
  shopId: number;
  lastUpdated: string;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface TestListItem {
  testId: string;
  testName: string;
  type: TestType;
  status: TestStatus;
  startDate: string;
  endDate: string | null;
  daysInTest: number;
  plannedDays: number;
  progressPercent: number;
  aiHypothesis: string;
  strategyName: string;
  strategyCatalogId: number | null;
  strategySecondaryCategory: string;
  hasWinner: boolean;
  segmentWinner: string | null;
  /** QuickSight segment filter label, e.g. "(03) First Time customers "; null when unmapped, absent until pdq-core deploys it */
  segmentWinnerClean?: string | null;
  outliersImplementation: string | null;
  impactAmount: number;
  impactMetric: string;
  statSig: number;
  formattedDateRange?: string;
  isRunning?: boolean;
  outliersDisplay?: string;
  maxxAnalysis?: MaxxAnalysisRowState;
}

// ============================================================================
// Request/Filter Interfaces
// ============================================================================

export interface TestFilters {
  startDate?: string;
  endDate?: string;
  status?: string;
  testType?: string;
  showWinnersOnly?: boolean;
  searchQuery?: string;
  sortColumn?: SortableColumn;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export const DEFAULT_PAGE_SIZE = 10;

// ============================================================================
// UI State Interfaces
// ============================================================================

export interface DropdownOption {
  value: string;
  label: string;
}

// ============================================================================
// Enums
// ============================================================================

export enum TestStatus {
  RUNNING = 'running',
  CONCLUDED = 'concluded',
  PAUSED = 'paused',
}

export enum TestType {
  EXTENSION = 'Extension',
  CROSS_SITE = 'Cross site',
  PROFILE = 'Profile',
  SHIPPING = 'Shipping',
}

// ============================================================================
// Constants
// ============================================================================

export const STATUS_OPTIONS: DropdownOption[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'running', label: 'Running' },
  { value: 'concluded', label: 'Concluded' },
];

export const TEST_TYPE_OPTIONS: DropdownOption[] = [
  { value: 'all', label: 'All Types' },
  { value: 'extension', label: 'Extension' },
  { value: 'cross_site', label: 'Cross site' },
  { value: 'profile', label: 'Profile' },
  { value: 'shipping', label: 'Shipping' },
];

// ============================================================================
// Helper Types
// ============================================================================

export type SortDirection = 'asc' | 'desc' | null;

export type SortableColumn =
  | 'testName'
  | 'type'
  | 'status'
  | 'strategyName'
  | 'segmentWinner'
  | 'impactAmount'
  | 'statSig';

export interface SortState {
  column: SortableColumn | null;
  direction: SortDirection;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export function getConfidenceLevel(score: number): ConfidenceLevel {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

// ============================================================================
// Column Configuration
// ============================================================================

export interface TableColumnOption {
  prop: string;
  name: string;
  sortKey?: SortableColumn;
  tooltip: string;
  cssClass: string;
}

export const COLUMN_OPTIONS: TableColumnOption[] = [
  {
    prop: 'testName',
    name: 'Test Name',
    sortKey: 'testName',
    tooltip: 'Test name, date range, and progress. Sorted by start date',
    cssClass: 'col-test-name',
  },
  {
    prop: 'type',
    name: 'Type',
    sortKey: 'type',
    tooltip: 'Test type: Shipping, Checkout, PDP, Cross-site, Extensions, Post Purchase',
    cssClass: 'col-type',
  },
  {
    prop: 'status',
    name: 'Status',
    sortKey: 'status',
    tooltip: 'Current test status: Running or Concluded',
    cssClass: 'col-status',
  },
  {
    prop: 'hypothesis',
    name: 'MAXX Hypothesis',
    tooltip: 'MAXX-generated hypothesis based on merchant data and industry patterns',
    cssClass: 'col-hypothesis',
  },
  {
    prop: 'strategy',
    name: 'Link Strategy',
    sortKey: 'strategyName',
    tooltip: 'Strategy name. Click to navigate to Strategy Playbook entry',
    cssClass: 'col-strategy',
  },
  {
    prop: 'winner',
    name: 'Winner',
    sortKey: 'segmentWinner',
    tooltip: 'Winning segment name from the test results',
    cssClass: 'col-winner',
  },
  {
    prop: 'impact',
    name: 'Impact',
    sortKey: 'impactAmount',
    tooltip: 'Annualized revenue impact based on test results',
    cssClass: 'col-impact',
  },
  {
    prop: 'statSig',
    name: 'Stat. Sig.',
    sortKey: 'statSig',
    tooltip: 'Statistical significance percentage. Green (\u226585%), Yellow (70-84%), Gray (<70%)',
    cssClass: 'col-stat-sig',
  },
];

export const LOCAL_STORAGE_COLUMNS_KEY = 'all-tests-dashboard-columns';

export function calculateElapsedDays(startDate: string): number {
  const today = new Date();
  const start = new Date(startDate);
  const elapsedMs = today.getTime() - start.getTime();
  return Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
}

export function formatDateRange(
  startDate: string,
  endDate: string | null,
  plannedDays: number,
  status: string,
): string {
  const dateFormat: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const start = new Date(startDate);
  const startStr = start.toLocaleDateString('en-US', dateFormat);

  if (status === 'concluded' && endDate) {
    const end = new Date(endDate);
    const endStr = end.toLocaleDateString('en-US', dateFormat);
    const durationMs = end.getTime() - start.getTime();
    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    return `${startStr} - ${endStr} (${days} days)`;
  }

  const elapsed = calculateElapsedDays(startDate);
  return `${startStr} - Ongoing (${elapsed}/${plannedDays} Days)`;
}
